import { DatePipe } from '@angular/common';
import type { ElementRef } from '@angular/core';
import {
  afterNextRender,
  Component,
  DOCUMENT,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import type { CookieConsent } from '@core/services';
import { CookieConsentService } from '@core/services';
import { Button } from '@shared/components';
import { BreakpointService } from '@shared/services';

@Component({
  selector: 'app-cookie-banner',
  imports: [RouterLink, Button, DatePipe],
  templateUrl: './cookie-banner.html',
  styleUrl: './cookie-banner.scss',
})
export class CookieBanner {
  private readonly consent = inject(CookieConsentService);
  private readonly document = inject(DOCUMENT);
  private readonly breakpoints = inject(BreakpointService);

  protected readonly visible = signal(false);
  protected readonly isMobile = this.breakpoints.isMobile;
  /** Existing decision when the banner is reopened; `null` on the first-time prompt. */
  protected readonly decision = signal<CookieConsent | null>(null);

  private readonly banner = viewChild<ElementRef<HTMLElement>>('banner');

  constructor() {
    // Consent lives in a cookie, so the banner is decided post-hydration (browser only)
    // to keep the server-rendered markup and the client in sync.
    afterNextRender(() => {
      if (!this.consent.hasConsent()) {
        this.show();
      }
    });

    // Content blockers (e.g. Brave Shields' cookie-notice blocking) hide consent banners with
    // `display: none`, which would leave the page scroll-locked behind an invisible dialog.
    // Lock scrolling only while the banner actually has a rendered box.
    effect((onCleanup) => {
      const banner = this.banner()?.nativeElement;
      if (!banner) {
        return;
      }

      const observer = new ResizeObserver(([entry]) => {
        const { width, height } = entry.contentRect;
        this.lockScroll(width > 0 && height > 0);
      });
      observer.observe(banner);

      onCleanup(() => {
        observer.disconnect();
        this.lockScroll(false);
      });
    });

    // Reopen on demand (e.g. from the footer or the Cookie Policy page). The initial
    // value is 0, so this only fires once the user actively requests it.
    effect(() => {
      if (this.consent.reopenRequests() > 0) {
        this.decision.set(this.consent.getConsent());
        this.show();
      }
    });
  }

  acceptAll(): void {
    this.consent.setConsent(true);

    this.dismiss();
  }

  rejectAll(): void {
    this.consent.setConsent(false);

    this.dismiss();
  }

  private dismiss(): void {
    this.visible.set(false);
    this.decision.set(null);
  }

  private show(): void {
    this.visible.set(true);
  }

  private lockScroll(locked: boolean): void {
    this.document.body.style.overflow = locked ? 'hidden' : '';
  }
}
